import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { searchDocumentation } from "../data/resourcesData";
import styles from "../ResourcesLayout.module.css";

/**
 * SearchModal — Full-screen documentation search overlay.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 */
const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to allow the modal to render
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Search on query change
  useEffect(() => {
    const r = searchDocumentation(query);
    setResults(r);
    setSelectedIndex(0);
  }, [query]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleNavigate(results[selectedIndex].path);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles["search-overlay"]} onClick={onClose} role="dialog" aria-modal="true" aria-label="Search documentation">
      <div className={styles["search-modal"]} onClick={(e) => e.stopPropagation()}>
        {/* Search input */}
        <div className={styles["search-input-wrapper"]}>
          <svg className={styles["search-input-icon"]} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className={styles["search-input"]}
            placeholder="Search documentation..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search documentation"
          />
          <button className={styles["search-close-btn"]} onClick={onClose} aria-label="Close search">
            <kbd>ESC</kbd>
          </button>
        </div>

        {/* Results */}
        {query.length >= 2 && (
          <div className={styles["search-results"]}>
            {results.length === 0 ? (
              <div className={styles["search-no-results"]}>
                No results found for "{query}"
              </div>
            ) : (
              <ul className={styles["search-results-list"]} role="listbox">
                {results.map((result, idx) => (
                  <li
                    key={result.slug}
                    className={`${styles["search-result-item"]} ${idx === selectedIndex ? styles["search-result-active"] : ""}`}
                    role="option"
                    aria-selected={idx === selectedIndex}
                    onClick={() => handleNavigate(result.path)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <div className={styles["search-result-title"]}>{result.title}</div>
                    <div className={styles["search-result-subtitle"]}>{result.subtitle}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Hint */}
        {query.length < 2 && (
          <div className={styles["search-hint"]}>
            Type at least 2 characters to search
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
